/**
 * Phase 10 — Public / authenticated Enterprise Inquiry submission page.
 * POSTs to /api/enterprise/inquiries. Submits with auth header when logged in.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Info } from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputStyle = {
  width: '100%', padding: '10px 12px',
  border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.sm,
  fontSize: 14, outline: 'none', boxSizing: 'border-box',
  background: theme.colors.bg.card, color: theme.colors.text.primary,
};
const label = { fontSize: 12, fontWeight: 700, color: theme.colors.text.primary, marginBottom: 4, display: 'block' };
const cardStyle = {
  background: theme.colors.bg.card,
  border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, padding: 28,
};

const initial = {
  requester_name: '', requester_email: '', company_name: '',
  website_or_social: '', phone: '', business_type: '',
  expected_volume: '', use_case: '', requested_features: '',
  budget_range: '', timeline: '', notes: '',
};

export default function EnterpriseInquiryPage() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.requester_name || !form.requester_email || !form.company_name) {
      toast.error('Name, email, and company are required');
      return;
    }
    setLoading(true);
    try {
      const headers = {};
      const token = localStorage.getItem('token');
      if (token) headers.Authorization = `Bearer ${token}`;
      await axios.post(`${API}/enterprise/inquiries`, form, { headers });
      setSubmitted(true);
      toast.success('Enterprise inquiry received');
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error(
          'Too many Enterprise inquiries were submitted from this connection. Please try again later.'
        );
      } else {
        toast.error(err?.response?.data?.detail || 'Submission failed');
      }
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div data-testid="enterprise-inquiry-success" style={{ padding: 40, maxWidth: 720, margin: '0 auto' }}>
        <div style={cardStyle}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
            <Building2 size={22} color={theme.colors.primary} />
            <h1 style={{ fontSize: 22, margin: 0, color: theme.colors.text.primary }}>Enterprise Inquiry Received</h1>
          </div>
          <p style={{ fontSize: 14, color: theme.colors.text.secondary }}>
            Your Enterprise inquiry has been received. Our team will review your
            request and follow up if it appears to be a fit.
          </p>
          <div style={{ marginTop: 14, padding: 10, background: '#FFFBEB',
            border: '1px solid #FDE68A', borderRadius: 6, color: '#78350F',
            fontSize: 12, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
            <Info size={13} style={{ marginTop: 2 }} />
            Enterprise access is reviewed manually. Submitting this form does not
            guarantee approval, pricing, availability, payouts, or custom
            integrations.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="enterprise-inquiry-page" style={{ padding: 40, maxWidth: 820, margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
        <Building2 size={26} color={theme.colors.primary} />
        <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0,
          fontFamily: theme.fonts.heading, color: theme.colors.text.primary }}>
          Enterprise Inquiry
        </h1>
      </div>
      <p style={{ fontSize: 14, color: theme.colors.text.secondary, marginBottom: 22 }}>
        Tell us about your organization, expected Kickback volume, and the type
        of support you need. Enterprise access is reviewed manually.
      </p>

      <form onSubmit={submit} style={cardStyle}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div><label style={label}>Name *</label>
            <input data-testid="ent-name" style={inputStyle}
              value={form.requester_name} onChange={set('requester_name')} /></div>
          <div><label style={label}>Email *</label>
            <input data-testid="ent-email" type="email" style={inputStyle}
              value={form.requester_email} onChange={set('requester_email')} /></div>
          <div><label style={label}>Company Name *</label>
            <input data-testid="ent-company" style={inputStyle}
              value={form.company_name} onChange={set('company_name')} /></div>
          <div><label style={label}>Website or Social Link</label>
            <input data-testid="ent-website" style={inputStyle}
              value={form.website_or_social} onChange={set('website_or_social')} /></div>
          <div><label style={label}>Phone</label>
            <input data-testid="ent-phone" style={inputStyle}
              value={form.phone} onChange={set('phone')} /></div>
          <div><label style={label}>Business Type</label>
            <input data-testid="ent-business-type" style={inputStyle}
              placeholder="retail / saas / venue / …"
              value={form.business_type} onChange={set('business_type')} /></div>
          <div><label style={label}>Expected Volume</label>
            <input data-testid="ent-volume" style={inputStyle}
              placeholder="# of holders / month"
              value={form.expected_volume} onChange={set('expected_volume')} /></div>
          <div><label style={label}>Budget Range</label>
            <input data-testid="ent-budget" style={inputStyle}
              value={form.budget_range} onChange={set('budget_range')} /></div>
          <div><label style={label}>Timeline</label>
            <input data-testid="ent-timeline" style={inputStyle}
              placeholder="Q2 / ASAP / …"
              value={form.timeline} onChange={set('timeline')} /></div>
        </div>
        <div style={{ marginTop: 10 }}>
          <label style={label}>Use Case</label>
          <textarea data-testid="ent-use-case" style={{ ...inputStyle, minHeight: 70, fontFamily: 'inherit' }}
            value={form.use_case} onChange={set('use_case')} />
        </div>
        <div style={{ marginTop: 10 }}>
          <label style={label}>Requested Features</label>
          <textarea data-testid="ent-features" style={{ ...inputStyle, minHeight: 70, fontFamily: 'inherit' }}
            value={form.requested_features} onChange={set('requested_features')} />
        </div>
        <div style={{ marginTop: 10 }}>
          <label style={label}>Notes</label>
          <textarea data-testid="ent-notes" style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit' }}
            value={form.notes} onChange={set('notes')} />
        </div>

        <div style={{ marginTop: 16, padding: 10, background: '#FFFBEB',
          border: '1px solid #FDE68A', borderRadius: 6, color: '#78350F',
          fontSize: 11, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
          <Info size={12} style={{ marginTop: 2 }} />
          Enterprise access is reviewed manually. Submitting this form does not
          guarantee approval, pricing, availability, payouts, or custom
          integrations.
        </div>

        <button data-testid="ent-submit" type="submit" disabled={loading}
          style={{ marginTop: 16, padding: '12px 20px', border: 'none',
            borderRadius: theme.radius.sm, background: theme.colors.primary,
            color: '#fff', fontWeight: 700, fontSize: 14,
            cursor: loading ? 'not-allowed' : 'pointer' }}>
          {loading ? 'Submitting…' : 'Submit Enterprise Inquiry'}
        </button>
      </form>
    </div>
  );
}
